/**
 * SaaS Spend Dashboard (A7Y-1495)
 *
 * Surfaces the Google-Sheets-based vendor billing ledger at
 * /admin/saas-spend. All data flows through /admin/saas-spend/* endpoints
 * served by a7y-api (Cloudflare Workers) — no Firebase Functions involved.
 *
 * Auto-refresh interval matches the backend 5 min cache TTL.
 */
import { PageHeader } from "@/components/PageHeader";
import {
  Anomaly,
  HeadlineMetrics,
  LEDGER_SHEET_URL,
  PipelineHealth,
  SpendByCategoryResponse,
  TransactionRow,
  TransactionsQuery,
  VendorRow,
  saasSpendApi,
} from "@/lib/saasSpendApi";
import { cn, formatCurrency } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  ExternalLink,
  FileText,
  Flame,
  Minus,
  Search,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { useMemo, useState } from "react";

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

function MetricCard({
  title,
  primary,
  secondary,
  delta,
  icon,
  tone = "default",
}: {
  title: string;
  primary: string;
  secondary?: string;
  delta?: { value: number; label: string } | null;
  icon: React.ReactNode;
  tone?: "default" | "warn";
}) {
  const deltaColor =
    delta == null
      ? "text-gray-400"
      : delta.value > 0
        ? "text-red-600 dark:text-red-400"
        : delta.value < 0
          ? "text-green-600 dark:text-green-400"
          : "text-gray-500";
  const DeltaIcon =
    delta == null ? Minus : delta.value > 0 ? ArrowUpRight : delta.value < 0 ? ArrowDownRight : Minus;
  return (
    <div
      className={cn(
        "rounded-lg p-6 shadow-sm border",
        tone === "warn"
          ? "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900"
          : "bg-white dark:bg-zinc-800 border-gray-200 dark:border-zinc-700",
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{primary}</p>
          {secondary && (
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">{secondary}</p>
          )}
          {delta && (
            <p className={cn("mt-2 text-sm flex items-center gap-1 font-medium", deltaColor)}>
              <DeltaIcon className="h-4 w-4" />
              {Math.abs(delta.value).toFixed(1)}% {delta.label}
            </p>
          )}
        </div>
        <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 text-green-600 dark:text-green-300">
          {icon}
        </div>
      </div>
    </div>
  );
}

function Sparkline({ data }: { data: Array<{ date: string; amount: number }> }) {
  const max = Math.max(1, ...data.map((d) => d.amount));
  return (
    <div className="flex h-12 items-end gap-px mt-2">
      {data.map((d) => (
        <div
          key={d.date}
          title={`${d.date}: ${formatCurrency(d.amount)}`}
          className="flex-1 bg-green-400 dark:bg-green-500 rounded-sm transition-colors"
          style={{ height: `${Math.max(2, (d.amount / max) * 100)}%`, minWidth: "2px" }}
        />
      ))}
    </div>
  );
}

function HeadlineSection({ data }: { data: HeadlineMetrics | undefined }) {
  if (!data) return <SectionSkeleton heightClass="h-48" />;
  return (
    <div className="grid gap-6 md:grid-cols-3 mb-8">
      <MetricCard
        title="Month-to-date"
        primary={formatCurrency(data.mtdSpend)}
        secondary={`vs ${formatCurrency(data.mtdComparisonSpend)} same-period last month`}
        delta={{ value: data.mtdDeltaPct, label: "vs LM" }}
        icon={<CalendarDays className="h-6 w-6" />}
      />
      <MetricCard
        title="Last 30 days"
        primary={formatCurrency(data.last30dSpend)}
        secondary={`vs ${formatCurrency(data.prev30dSpend)} prior 30d`}
        delta={{ value: data.last30dDelta7dPct, label: "vs prior 7d" }}
        icon={<TrendingUp className="h-6 w-6" />}
      />
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Daily burn rate</p>
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              {formatCurrency(data.dailyBurnRate)}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">trailing 60d</p>
            <Sparkline data={data.burnSparkline60d} />
          </div>
          <div className="rounded-full bg-green-100 dark:bg-green-900/30 p-3 text-green-600 dark:text-green-300">
            <Flame className="h-6 w-6" />
          </div>
        </div>
      </div>
    </div>
  );
}

const CATEGORY_PALETTE = [
  "bg-green-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-cyan-500",
  "bg-indigo-500",
  "bg-fuchsia-500",
  "bg-emerald-500",
  "bg-orange-500",
] as const;

function CategorySection({ data }: { data: SpendByCategoryResponse | undefined }) {
  const byMonth = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    if (!data) return m;
    for (const month of data.months) m.set(month, new Map());
    for (const cell of data.cells) {
      m.get(cell.month)?.set(cell.category, cell.amount);
    }
    return m;
  }, [data]);

  const maxMonthTotal = useMemo(() => {
    if (!data) return 1;
    let max = 0;
    for (const month of data.months) {
      const m = byMonth.get(month);
      if (!m) continue;
      let total = 0;
      for (const v of m.values()) total += v;
      if (total > max) max = total;
    }
    return max || 1;
  }, [byMonth, data]);

  if (!data) return <SectionSkeleton heightClass="h-72" />;
  const palette = CATEGORY_PALETTE;

  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Spend by category — last 6 months
      </h2>
      <div className="flex items-end gap-4 h-64">
        {data.months.map((month) => {
          const m = byMonth.get(month) || new Map();
          let total = 0;
          for (const v of m.values()) total += v;
          const heightPct = (total / maxMonthTotal) * 100;
          return (
            <div key={month} className="flex flex-col items-center flex-1">
              <div className="text-xs text-gray-500 dark:text-zinc-500 mb-1">
                {formatCurrency(total)}
              </div>
              <div
                className="relative w-full max-w-[60px] rounded-t overflow-hidden flex flex-col-reverse"
                style={{ height: `${Math.max(2, heightPct)}%` }}
              >
                {data.categories.map((cat, i) => {
                  const amt = m.get(cat) || 0;
                  if (amt === 0 || total === 0) return null;
                  const pct = (amt / total) * 100;
                  return (
                    <div
                      key={cat}
                      className={cn(palette[i % palette.length])}
                      style={{ height: `${pct}%` }}
                      title={`${cat}: ${formatCurrency(amt)}`}
                    />
                  );
                })}
              </div>
              <div className="text-xs text-gray-600 dark:text-zinc-400 mt-2 font-medium">
                {month}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-zinc-700">
        {data.categories.map((cat, i) => (
          <div key={cat} className="flex items-center gap-2 text-xs">
            <span className={cn("w-3 h-3 rounded-sm", palette[i % palette.length])} />
            <span className="text-gray-700 dark:text-zinc-300">{cat}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TopVendorsSection({
  data,
  onSelectVendor,
}: {
  data: VendorRow[] | undefined;
  onSelectVendor: (vendor: string) => void;
}) {
  if (!data) return <SectionSkeleton heightClass="h-72" />;
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Top vendors</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-500 border-b border-gray-200 dark:border-zinc-700">
              <th className="pb-2 pr-3">Vendor</th>
              <th className="pb-2 pr-3 text-right">MTD</th>
              <th className="pb-2 pr-3 text-right">30d %</th>
              <th className="pb-2 pr-3 text-right">YoY %</th>
              <th className="pb-2 text-right">Trend</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr
                key={row.vendor}
                onClick={() => onSelectVendor(row.vendor)}
                className="border-b border-gray-100 dark:border-zinc-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-700/50"
              >
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                  {row.vendor}
                </td>
                <td className="py-2 pr-3 text-right text-gray-900 dark:text-white">
                  {formatCurrency(row.mtdSpend)}
                </td>
                <td
                  className={cn(
                    "py-2 pr-3 text-right",
                    row.delta30dPct > 5
                      ? "text-red-600 dark:text-red-400"
                      : row.delta30dPct < -5
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-500",
                  )}
                >
                  {row.delta30dPct > 0 ? "+" : ""}
                  {row.delta30dPct.toFixed(1)}%
                </td>
                <td
                  className={cn(
                    "py-2 pr-3 text-right",
                    row.deltaYoYPct > 5
                      ? "text-red-600 dark:text-red-400"
                      : row.deltaYoYPct < -5
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-500",
                  )}
                >
                  {row.deltaYoYPct > 0 ? "+" : ""}
                  {row.deltaYoYPct.toFixed(1)}%
                </td>
                <td className="py-2 text-right">
                  {row.trend === "up" ? (
                    <ArrowUpRight className="h-4 w-4 text-red-500 inline" />
                  ) : row.trend === "down" ? (
                    <ArrowDownRight className="h-4 w-4 text-green-500 inline" />
                  ) : (
                    <Minus className="h-4 w-4 text-gray-400 inline" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-zinc-500 py-6 text-center">
            No vendor activity in the current window.
          </p>
        )}
      </div>
    </div>
  );
}

function AnomalySection({ data }: { data: Anomaly[] | undefined }) {
  if (!data) return <SectionSkeleton heightClass="h-32" />;
  if (data.length === 0) {
    return (
      <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Anomalies
        </h2>
        <p className="text-sm text-gray-500 dark:text-zinc-500">No anomalies detected.</p>
      </div>
    );
  }
  const iconFor = (kind: Anomaly["kind"]) =>
    kind === "spike" ? <Flame className="h-5 w-5" /> :
    kind === "new" ? <Sparkles className="h-5 w-5" /> :
    <CalendarDays className="h-5 w-5" />;
  const toneFor = (kind: Anomaly["kind"]) =>
    kind === "spike"
      ? "bg-rose-50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-900 text-rose-700 dark:text-rose-300"
      : kind === "new"
        ? "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300"
        : "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300";
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Anomalies</h2>
      <div className="space-y-3">
        {data.map((a, i) => (
          <div
            key={i}
            className={cn(
              "flex items-start gap-3 rounded-lg border px-4 py-3",
              toneFor(a.kind),
            )}
          >
            <div className="mt-0.5">{iconFor(a.kind)}</div>
            <div className="flex-1">
              <p className="font-medium">
                {a.vendor}
                <span className="ml-2 text-xs uppercase tracking-wide opacity-75">
                  {a.kind}
                </span>
              </p>
              <p className="text-sm opacity-90 mt-0.5">{a.message}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TransactionsSection({
  data,
  filters,
  setFilters,
  vendorOptions,
  categoryOptions,
  typeOptions,
}: {
  data: TransactionRow[] | undefined;
  filters: TransactionsQuery;
  setFilters: (f: TransactionsQuery) => void;
  vendorOptions: string[];
  categoryOptions: string[];
  typeOptions: string[];
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Transactions ({data?.length ?? "—"})
      </h2>
      <div className="grid gap-3 md:grid-cols-7 mb-4">
        <input
          type="date"
          value={filters.from || ""}
          onChange={(e) => setFilters({ ...filters, from: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
          placeholder="From"
        />
        <input
          type="date"
          value={filters.to || ""}
          onChange={(e) => setFilters({ ...filters, to: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        />
        <select
          value={filters.vendor || ""}
          onChange={(e) => setFilters({ ...filters, vendor: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        >
          <option value="">All vendors</option>
          {vendorOptions.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filters.category || ""}
          onChange={(e) => setFilters({ ...filters, category: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        >
          <option value="">All categories</option>
          {categoryOptions.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={filters.type || ""}
          onChange={(e) => setFilters({ ...filters, type: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        >
          <option value="">All types</option>
          {typeOptions.map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
        <input
          type="number"
          placeholder="Min $"
          value={filters.minAmount ?? ""}
          onChange={(e) => setFilters({ ...filters, minAmount: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        />
        <input
          type="number"
          placeholder="Max $"
          value={filters.maxAmount ?? ""}
          onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white px-2 py-1 col-span-1"
        />
      </div>
      <div className="relative mb-4">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search vendor, description, transaction ID…"
          value={filters.search || ""}
          onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
          className="text-sm rounded border-gray-300 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white pl-9 pr-3 py-2 w-full"
        />
      </div>
      <div className="overflow-x-auto max-h-[600px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white dark:bg-zinc-800">
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-zinc-500 border-b border-gray-200 dark:border-zinc-700">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Vendor</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Category</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Description</th>
              <th className="py-2 pr-3">Invoice</th>
              <th className="py-2 w-8" />
            </tr>
          </thead>
          <tbody>
            {(data || []).map((r, i) => (
              <tr
                key={`${r.transactionId}-${r.rowNumber}-${i}`}
                className="border-b border-gray-100 dark:border-zinc-700"
              >
                <td className="py-2 pr-3 text-gray-700 dark:text-zinc-300 whitespace-nowrap">
                  {r.date || "—"}
                </td>
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                  {r.vendor}
                </td>
                <td className="py-2 pr-3 text-right text-gray-900 dark:text-white">
                  {formatCurrency(r.amount)}
                </td>
                <td className="py-2 pr-3 text-gray-600 dark:text-zinc-400">{r.category}</td>
                <td className="py-2 pr-3 text-gray-600 dark:text-zinc-400">{r.type}</td>
                <td className="py-2 pr-3 text-gray-600 dark:text-zinc-400">
                  {r.description}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  {r.invoiceUrl ? (
                    <a
                      href={r.invoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      title="Open the archived invoice PDF"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400"
                    >
                      <FileText className="h-4 w-4" />
                      PDF
                    </a>
                  ) : (
                    <span className="text-gray-400 dark:text-zinc-500" title="No invoice PDF filed for this row">
                      —
                    </span>
                  )}
                </td>
                <td className="py-2">
                  <a
                    href={r.deepLink}
                    target="_blank"
                    rel="noreferrer"
                    title={`Sheet row ${r.rowNumber}`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 inline-block"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && data.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-zinc-500 py-6 text-center">
            No transactions match your filters.
          </p>
        )}
      </div>
    </div>
  );
}

function PipelineHealthFooter({ data }: { data: PipelineHealth | undefined }) {
  if (!data) return null;
  const toneClass =
    data.status === "red"
      ? "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900 text-red-700 dark:text-red-300"
      : data.status === "yellow"
        ? "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900 text-amber-700 dark:text-amber-300"
        : "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-900 text-green-700 dark:text-green-300";
  const lastSyncText =
    data.lastIngestDate == null
      ? "no ingest data"
      : `last ingest ${data.lastIngestDate}${
          data.daysSinceLastIngest != null ? ` (${data.daysSinceLastIngest}d ago)` : ""
        }`;
  return (
    <div className={cn("rounded-lg border px-5 py-3 flex items-center justify-between text-sm", toneClass)}>
      <div>
        <span className="font-medium">Pipeline health:</span> {lastSyncText} ·{" "}
        {data.totalRows} ledger rows
      </div>
      <div className="text-xs opacity-80">
        Reminder: manual-PDF vendors still require Yevhen to drop receipts into Drive.
      </div>
    </div>
  );
}

function SectionSkeleton({ heightClass }: { heightClass: string }) {
  return (
    <div
      className={cn(
        "rounded-lg bg-white dark:bg-zinc-800 shadow-sm border border-gray-200 dark:border-zinc-700 mb-8 animate-pulse",
        heightClass,
      )}
    />
  );
}

export function SaasSpend() {
  const [filters, setFilters] = useState<TransactionsQuery>({});

  const headlineQ = useQuery({
    queryKey: ["saas-spend", "headline"],
    queryFn: saasSpendApi.getHeadline,
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const categoryQ = useQuery({
    queryKey: ["saas-spend", "by-category"],
    queryFn: saasSpendApi.getByCategory,
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const vendorsQ = useQuery({
    queryKey: ["saas-spend", "top-vendors"],
    queryFn: () => saasSpendApi.getTopVendors(),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const anomaliesQ = useQuery({
    queryKey: ["saas-spend", "anomalies"],
    queryFn: saasSpendApi.getAnomalies,
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const txQ = useQuery({
    queryKey: ["saas-spend", "transactions", filters],
    queryFn: () => saasSpendApi.getTransactions(filters),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const healthQ = useQuery({
    queryKey: ["saas-spend", "pipeline-health"],
    queryFn: saasSpendApi.getPipelineHealth,
    refetchInterval: REFRESH_INTERVAL_MS,
  });

  const isRefreshing =
    headlineQ.isRefetching ||
    categoryQ.isRefetching ||
    vendorsQ.isRefetching ||
    anomaliesQ.isRefetching ||
    txQ.isRefetching ||
    healthQ.isRefetching;

  const firstLoadError =
    headlineQ.error || categoryQ.error || vendorsQ.error || anomaliesQ.error || healthQ.error;

  // Derive dropdown options from the existing transactions response so we don't
  // need a dedicated metadata endpoint.
  const vendorOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of txQ.data || []) if (t.vendor) set.add(t.vendor);
    return Array.from(set).sort();
  }, [txQ.data]);
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of txQ.data || []) if (t.category) set.add(t.category);
    return Array.from(set).sort();
  }, [txQ.data]);
  const typeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const t of txQ.data || []) if (t.type) set.add(t.type);
    return Array.from(set).sort();
  }, [txQ.data]);

  return (
    <div className="px-4 py-0">
      <PageHeader title="SaaS Spend" subtitle="Vendor billing ledger — populated by /a7y-update-saas-ledger">
        <div className="flex items-center gap-3">
          {isRefreshing && (
            <span className="text-xs text-gray-500 dark:text-zinc-500">refreshing…</span>
          )}
          <a
            href={LEDGER_SHEET_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400"
          >
            Edit in sheet <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </PageHeader>

      {firstLoadError && (
        <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300 mb-6">
          Failed to load some sections: {String((firstLoadError as Error).message)}
        </div>
      )}

      <HeadlineSection data={headlineQ.data} />
      <CategorySection data={categoryQ.data} />
      <TopVendorsSection
        data={vendorsQ.data}
        onSelectVendor={(vendor) => setFilters((f) => ({ ...f, vendor }))}
      />
      <AnomalySection data={anomaliesQ.data} />
      <TransactionsSection
        data={txQ.data}
        filters={filters}
        setFilters={setFilters}
        vendorOptions={vendorOptions}
        categoryOptions={categoryOptions}
        typeOptions={typeOptions}
      />
      <PipelineHealthFooter data={healthQ.data} />
    </div>
  );
}

export default SaasSpend;
